import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  getOnboardingState,
  saveOnboardingState,
  SchoolData,
  OnboardingState,
  generateAndSaveStaffCodes,
  saveTeacherCredentials,
  saveSchoolCredentials,
} from '@/lib/onboarding-store';
import { WelcomeScreen } from './WelcomeScreen';
import { TeacherLogin } from './TeacherLogin';
import { TeacherPasswordSetup } from './TeacherPasswordSetup';
import { SchoolStep1Verify } from './SchoolStep1Verify';
import { SchoolStep2Contact } from './SchoolStep2Contact';
import { SchoolStep3Configure } from './SchoolStep3Configure';
import { SchoolStep3Staff } from './SchoolStep3Staff';
import { SchoolStep4Policies } from './SchoolStep4Policies';
import { SchoolStepPassword } from './SchoolStepPassword';
import SchoolStepReview from './SchoolStepReview';
import { SetupComplete } from './SetupComplete';

type WizardStep =
  | 'welcome'
  | 'teacher-login'
  | 'teacher-password'
  | 'cover-login'
  | 'cover-password'
  | 'school-1'
  | 'school-2'
  | 'school-password'
  | 'school-3'
  | 'school-4'
  | 'school-5'
  | 'school-review'
  | 'complete';

interface OnboardingWizardProps {
  onComplete: () => void;
}

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState>(getOnboardingState);
  // Lazy initial: derive the right starting step from storage so a returning
  // wizard user never flashes the welcome animation when resuming mid-flow.
  const [currentStep, setCurrentStep] = useState<WizardStep>(() => {
    const saved = getOnboardingState();
    if (saved.userType === 'school' && saved.currentStep > 0) {
      return `school-${saved.currentStep}` as WizardStep;
    }
    // Used to resume on the teacher welcome animation. With that screen gone
    // there is nothing to resume ONTO — returning 'teacher-welcome' now renders
    // an empty page — and a teacher who has a name stored but isn't complete
    // belongs back at login, which is where 'welcome' leads.
    return 'welcome';
  });
  const [returningFromReview, setReturningFromReview] = useState(false);

  // Bail out early if onboarding is already complete (callsite shows the app instead)
  useEffect(() => {
    const saved = getOnboardingState();
    if (saved.isComplete) onComplete();
  }, [onComplete]);

  // Save state whenever it changes
  useEffect(() => {
    saveOnboardingState(state);
  }, [state]);

  const updateSchoolData = useCallback((field: keyof SchoolData, value: unknown) => {
    setState(prev => ({
      ...prev,
      schoolData: {
        ...prev.schoolData,
        [field]: value,
      },
    }));
  }, []);

  const handleCoverLogin = (coverName: string, email: string, department: string, _hasPassword: boolean, accessToken?: string, _userId?: string, _role?: string, schoolId?: string) => {
    if (accessToken) sessionStorage.setItem('cmc_teacher_token', accessToken);
    if (schoolId) sessionStorage.setItem('cmc_cover_school_id', schoolId);
    setState(prev => ({
      ...prev,
      userType: 'cover',
      teacherName: coverName,
      teacherEmail: email,
      teacherDepartment: department,
    }));
    finishOnboarding();
  };

  const handleCoverPasswordSetup = (password: string) => {
    saveTeacherCredentials({
      code: state.teacherCode,
      email: state.teacherEmail,
      password,
      name: state.teacherName,
      department: state.teacherDepartment,
    });
    setState(prev => ({ ...prev, teacherPassword: password }));
    finishOnboarding();
  };

  /**
   * What the welcome animations' onComplete used to do — teacher and cover.
   *
   * Both are gone — a ~5s branded teacher intro and a 12s six-phase cover one
   * are not MVP work — but their
   * side effects were load-bearing: Index reads isComplete from localStorage to
   * decide whether to mount CMCApp, so skipping the screen without this would
   * leave a logged-in teacher stuck on the wizard. Persist BEFORE notifying the
   * parent, exactly as the animation did.
   */
  const finishOnboarding = () => {
    saveOnboardingState({ isComplete: true });
    setState(prev => ({ ...prev, isComplete: true }));
    onComplete();
  };

  const handleTeacherLogin = (teacherName: string, email: string, department: string, hasPassword: boolean, accessToken?: string, _userId?: string, _role?: string, _schoolId?: string) => {
    if (accessToken) sessionStorage.setItem('cmc_teacher_token', accessToken);
    setState(prev => ({
      ...prev,
      teacherName,
      teacherEmail: email,
      teacherDepartment: department,
    }));
    if (hasPassword) {
      finishOnboarding();
    } else {
      setCurrentStep('teacher-password');
    }
  };

  const handlePasswordSetup = (password: string) => {
    // Save credentials for future logins
    saveTeacherCredentials({
      code: state.teacherCode,
      email: state.teacherEmail,
      password,
      name: state.teacherName,
      department: state.teacherDepartment,
    });
    setState(prev => ({ ...prev, teacherPassword: password }));
    finishOnboarding();
  };

  const handlePasswordSetupSchool = (password: string) => {
    const contactEmail = state.schoolData.contactEmail || '';
    saveSchoolCredentials({
      email: contactEmail,
      password,
      schoolName: state.schoolData.schoolName || 'Your School',
      createdAt: Date.now(),
    });
    setState(prev => ({ ...prev, schoolData: { ...prev.schoolData, adminPassword: password } }));
    setCurrentStep('school-3');
    setState(prev => ({ ...prev, currentStep: 3 }));
  };

  const handleSchoolComplete = () => {
    // Generate and save secure invite codes for all staff members
    const staff = state.schoolData.staff || [];
    if (staff.length > 0) {
      const codes = generateAndSaveStaffCodes(staff);
      console.log('[School Setup Complete] Generated invite codes:', codes.map(c => ({ name: c.name, code: c.code })));
    }
    // Credentials were already saved in the dedicated password step — no random generation needed.
    // Mark isComplete so that the restore-step logic in useEffect([onComplete]) knows not to
    // jump back to an earlier wizard step if something triggers a re-render while on SetupComplete.
    // The actual session/registration happens in SetupComplete; this flag is safe to set early
    // because the useEffect([onComplete]) in this component won't call onComplete() prematurely —
    // onComplete is a stable useCallback reference so the effect only fires once on mount
    // (before this flag is set), never again.
    setState(prev => ({ ...prev, isComplete: true }));
    setCurrentStep('complete');
  };

  const handleReviewEditStep = (step: number) => {
    setReturningFromReview(true);
    switch (step) {
      case 1: goToSchoolStep(1); break;
      case 2: goToSchoolStep(2); break;
      case 3: setCurrentStep('school-password'); break;
      case 4: goToSchoolStep(3); break;
      case 5: goToSchoolStep(4); break;
      case 6: goToSchoolStep(5); break;
    }
  };

  const returnToReview = () => {
    setReturningFromReview(false);
    setCurrentStep('school-review');
  };

  const goToSchoolStep = (step: number) => {
    setCurrentStep(`school-${step}` as WizardStep);
    setState(prev => ({ ...prev, currentStep: step }));
  };

  // Page transition variants
  // X1: step changes felt like ~2s of blank cream. mode="wait" runs the exit to
  // completion BEFORE the enter starts, so a 0.3s out + 0.3s in already showed
  // 0.6s of empty background — and WizardStepLayout then faded its own content
  // in over another 0.4s on top. Halving both, and shortening the travel, keeps
  // the sense of movement without the dead air.
  const pageVariants = {
    initial: { opacity: 0, x: 12 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -12 },
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentStep}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.16, ease: 'easeOut' }}
        className="min-h-screen"
      >
        {currentStep === 'welcome' && (
          <WelcomeScreen onComplete={() => navigate('/school/login')} />
        )}

        {currentStep === 'teacher-login' && (
          <TeacherLogin
            onLogin={handleTeacherLogin}
            onBack={() => navigate('/school/login')}
            enableQuickLogin={false}
          />
        )}

        {currentStep === 'teacher-password' && (
          <TeacherPasswordSetup
            teacherName={state.teacherName}
            onComplete={handlePasswordSetup}
          />
        )}


        {currentStep === 'cover-login' && (
          <TeacherLogin
            onLogin={handleCoverLogin}
            onBack={() => navigate('/school/login')}
            enableQuickLogin={false}
          />
        )}

        {currentStep === 'cover-password' && (
          <TeacherPasswordSetup
            teacherName={state.teacherName}
            onComplete={handleCoverPasswordSetup}
          />
        )}

        {currentStep === 'school-1' && (
          <SchoolStep1Verify
            data={state.schoolData}
            onChange={updateSchoolData}
            onNext={() => goToSchoolStep(2)}
            onBack={() => navigate('/school/login')}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-2' && (
          <SchoolStep2Contact
            data={state.schoolData}
            onChange={updateSchoolData}
            onNext={() => setCurrentStep('school-password')}
            onBack={() => goToSchoolStep(1)}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-password' && (
          <SchoolStepPassword
            contactEmail={state.schoolData.contactEmail || ''}
            existingPassword={state.schoolData.adminPassword}
            onNext={handlePasswordSetupSchool}
            onBack={() => goToSchoolStep(2)}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-3' && (
          <SchoolStep3Configure
            data={state.schoolData}
            onChange={updateSchoolData}
            onNext={() => goToSchoolStep(4)}
            onBack={() => setCurrentStep('school-password')}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-4' && (
          <SchoolStep3Staff
            data={state.schoolData}
            onChange={updateSchoolData}
            onNext={() => goToSchoolStep(5)}
            onBack={() => goToSchoolStep(3)}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-5' && (
          <SchoolStep4Policies
            data={state.schoolData}
            onChange={updateSchoolData}
            onNext={() => setCurrentStep('school-review')}
            onBack={() => goToSchoolStep(4)}
            onReturnToReview={returningFromReview ? returnToReview : undefined}
          />
        )}

        {currentStep === 'school-review' && (
          <SchoolStepReview
            data={state.schoolData}
            onConfirm={handleSchoolComplete}
            onEditStep={handleReviewEditStep}
          />
        )}

        {currentStep === 'complete' && (
          <SetupComplete
            schoolName={state.schoolData.schoolName || 'Your School'}
            staffCount={state.schoolData.staff?.length || 0}
            onContinue={onComplete}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
